import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PiArrowLeftBold, PiUserBold, PiCheckCircleBold, PiClockBold, PiLockSimpleBold } from 'react-icons/pi';
import { PageHeader } from '@v2/design-system';
import api from '@v2/lib/api';

type CommissionRecord = {
    id: number;
    ploomes_deal_id: number;
    deal_number: number | null;
    deal_title: string | null;
    deal_amount: number | null;
    user_id: number;
    user_name: string | null;
    commission_role: string;
    role_label: string;
    base_amount: number;
    gross_amount: number;
    final_amount: number;
    status: string;
    status_label: string;
    status_color: string;
    installment_label: string | null;
    commission_installment_id: number | null;
    confidential: boolean;
    notes: string | null;
    created_at: string;
};

const fmtBrl = (v: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

const STATUS_BADGE: Record<string, string> = {
    paid: 'bg-emerald-100 text-emerald-800',
    awaiting_nf: 'bg-amber-100 text-amber-800',
    nf_submitted: 'bg-blue-100 text-blue-800',
    pending: 'bg-gray-100 text-gray-700',
    cancelled: 'bg-red-100 text-red-700',
};

export default function CommissionDealDetailPage() {
    const { dealId } = useParams<{ dealId: string }>();

    const { data, isLoading, isError } = useQuery<{ data: CommissionRecord[]; meta: { total: number } }>({
        queryKey: ['commissioning', 'records', 'deal', dealId],
        queryFn: () =>
            api
                .get('/commissioning/records', { params: { deal_id: dealId, per_page: 100 } })
                .then((r) => r.data),
        enabled: !!dealId,
    });

    const records = data?.data ?? [];
    const deal = records[0];
    const totalCommission = records.reduce((sum, r) => sum + r.final_amount, 0);
    const dealTitle = deal?.deal_title ?? `Deal #${deal?.deal_number ?? dealId}`;

    const sellerRecords = records.filter((r) => !r.commission_installment_id);
    const installmentRecords = records.filter((r) => !!r.commission_installment_id);

    return (
        <div className="space-y-5">
            <div className="flex items-center gap-3">
                <Link
                    to="/commissioning/records"
                    className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600 hover:border-orange-300 hover:text-orange-600"
                >
                    <PiArrowLeftBold className="size-4" />
                    Voltar
                </Link>
            </div>

            <PageHeader
                title={dealTitle}
                subtitle={deal ? `Deal #${deal.deal_number} · Comissões` : 'Carregando...'}
            />

            {/* KPIs */}
            {deal && (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Valor do deal</p>
                        <p className="text-lg font-bold text-gray-900">{deal.deal_amount ? fmtBrl(deal.deal_amount) : '—'}</p>
                    </div>
                    <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Total de comissões</p>
                        <p className="text-lg font-bold text-emerald-700">{fmtBrl(totalCommission)}</p>
                    </div>
                    <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Registros</p>
                        <p className="text-lg font-bold text-gray-900">{records.length}</p>
                    </div>
                    <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Parcelas pagas</p>
                        <p className="text-lg font-bold text-gray-900">
                            {records.filter((r) => r.status === 'paid').length} / {records.length}
                        </p>
                    </div>
                </div>
            )}

            {isLoading && (
                <div className="animate-pulse space-y-3">
                    {[...Array(3)].map((_, i) => (
                        <div key={i} className="h-16 rounded-xl bg-gray-100" />
                    ))}
                </div>
            )}

            {isError && (
                <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                    Não foi possível carregar as comissões deste deal.
                </p>
            )}

            {records.length === 0 && !isLoading && (
                <p className="rounded-xl border border-dashed border-gray-300 px-6 py-8 text-center text-sm text-gray-400">
                    Nenhuma comissão registrada para este deal.
                </p>
            )}

            {/* Comissões diretas do deal (sem parcela) */}
            {sellerRecords.length > 0 && (
                <section className="space-y-2">
                    <h2 className="text-xs font-bold uppercase tracking-widest text-gray-400">Comissões do deal</h2>
                    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                        {sellerRecords.map((r, i) => (
                            <RecordRow key={r.id} record={r} isLast={i === sellerRecords.length - 1} />
                        ))}
                    </div>
                </section>
            )}

            {/* Comissões por parcela (gerente/diretor) */}
            {installmentRecords.length > 0 && (
                <section className="space-y-2">
                    <h2 className="text-xs font-bold uppercase tracking-widest text-gray-400">Comissões por parcela</h2>
                    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                        {installmentRecords.map((r, i) => (
                            <RecordRow key={r.id} record={r} isLast={i === installmentRecords.length - 1} />
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
}

function RecordRow({ record: r, isLast }: { record: CommissionRecord; isLast: boolean }) {
    const badgeCls = STATUS_BADGE[r.status] ?? 'bg-gray-100 text-gray-700';

    return (
        <div className={`flex items-center gap-3 px-4 py-3 ${!isLast ? 'border-b border-gray-100' : ''}`}>
            {/* Ícone de status */}
            <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-gray-100">
                {r.status === 'paid' ? (
                    <PiCheckCircleBold className="size-4 text-emerald-600" />
                ) : r.confidential ? (
                    <PiLockSimpleBold className="size-4 text-gray-400" />
                ) : (
                    <PiClockBold className="size-4 text-amber-500" />
                )}
            </div>

            {/* Nome + role */}
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                    <Link
                        to={`/commissioning/sellers/${r.user_id}`}
                        className="flex items-center gap-1 text-sm font-semibold text-gray-900 hover:text-orange-600"
                    >
                        <PiUserBold className="size-3.5 text-gray-400" />
                        {r.user_name ?? `Usuário #${r.user_id}`}
                    </Link>
                    {r.confidential && (
                        <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-bold text-gray-500">
                            Confidencial
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[11px] text-gray-400">{r.role_label}</span>
                    {r.installment_label && (
                        <span className="rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold text-blue-600">
                            Parcela {r.installment_label}
                        </span>
                    )}
                </div>
            </div>

            {/* Valores */}
            <div className="shrink-0 text-right space-y-0.5">
                <p className="text-sm font-bold text-emerald-700">{fmtBrl(r.final_amount)}</p>
                {r.base_amount !== r.final_amount && (
                    <p className="text-[10px] text-gray-400">base: {fmtBrl(r.base_amount)}</p>
                )}
            </div>

            {/* Status badge */}
            <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${badgeCls}`}>
                {r.status_label}
            </span>
        </div>
    );
}
